import {Alert, Button, Drawer, Group, Paper, Radio, Stack, Text} from '@mantine/core'
import {IconCheck, IconTruck} from '@tabler/icons-react'
import {useState} from 'react'

import type {DriverJob} from './schemas'

type DriverJobAssetAssignment = DriverJob['assetAssignments'][number]
type DriverJobLocation = DriverJob['locations'][number]

type DriverEquipmentCardProps = {
    assignment: DriverJobAssetAssignment
    locations: DriverJobLocation[]
    deploying: boolean
    onMarkDelivered?: (jobLocationId: string) => Promise<void>
    isPickup?: boolean
}

export function DriverEquipmentCard({
    assignment,
    locations,
    deploying,
    onMarkDelivered,
    isPickup = false,
}: DriverEquipmentCardProps) {
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
        locations.length === 1 ? locations[0].id : null,
    )

    const assetName = assignment.rentalAssetName
    const isDeployed = assignment.deployment.status === 'deployed'
    const isReturned = assignment.deployment.status === 'returned'
    const canMarkDelivered =
        onMarkDelivered != null && !isPickup && !isDeployed && !isReturned && locations.length > 0

    function openDrawer() {
        setSelectedLocationId(locations.length === 1 ? locations[0].id : null)
        setError(null)
        setDrawerOpen(true)
    }

    async function handleConfirm() {
        if (!selectedLocationId || !onMarkDelivered) {
            return
        }
        setError(null)
        try {
            await onMarkDelivered(selectedLocationId)
            setDrawerOpen(false)
        } catch (caught) {
            setError(
                caught instanceof Error
                    ? caught.message
                    : 'Could not save delivery. Check your signal and try again',
            )
        }
    }

    return (
        <>
            <Paper withBorder p="md" radius="md">
                <Stack gap="sm">
                    <Group gap="sm" wrap="nowrap">
                        <IconTruck size={22} stroke={1.5} />
                        <Stack gap={2} style={{minWidth: 0}}>
                            <Text size="sm" fw={600}>
                                {assetName}
                            </Text>
                            {isReturned ? (
                                <Group gap={6} wrap="nowrap">
                                    <IconCheck size={16} color="var(--mantine-color-gray-6)" />
                                    <Text size="sm" c="dimmed">
                                        {isPickup ? 'Collected' : 'Came back'}
                                    </Text>
                                </Group>
                            ) : isDeployed && isPickup ? (
                                <Text size="sm" fw={500}>
                                    To collect · {assignment.deployment.locationLabel}
                                </Text>
                            ) : isDeployed && !assignment.deployment.confirmed ? (
                                <Text size="sm" c="dimmed">
                                    Assigned
                                </Text>
                            ) : isDeployed ? (
                                <Group gap={6} wrap="nowrap">
                                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                                    <Text size="sm" c="green.7">
                                        Delivered · {assignment.deployment.locationLabel}
                                    </Text>
                                </Group>
                            ) : (
                                <Text size="sm" c="dimmed">
                                    {isPickup ? 'Not on site' : 'Pending delivery'}
                                </Text>
                            )}
                        </Stack>
                    </Group>
                    {canMarkDelivered && (
                        <Button fullWidth onClick={openDrawer} loading={deploying}>
                            Mark delivered
                        </Button>
                    )}
                </Stack>
            </Paper>

            <Drawer
                opened={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                position="bottom"
                title={`Deliver ${assetName}`}
            >
                <Stack gap="md">
                    <Text size="sm">Where did you drop off this equipment?</Text>
                    {locations.length > 1 ? (
                        <Radio.Group
                            value={selectedLocationId}
                            onChange={setSelectedLocationId}
                            name="delivery-location"
                        >
                            <Stack gap="xs">
                                {locations.map((location) => (
                                    <Radio
                                        key={location.id}
                                        value={location.id}
                                        label={location.name}
                                    />
                                ))}
                            </Stack>
                        </Radio.Group>
                    ) : locations.length === 1 ? (
                        <Text size="sm" fw={500}>
                            {locations[0].name}
                        </Text>
                    ) : null}
                    {error ? (
                        <Alert color="red" title="Not marked delivered">
                            {error}
                        </Alert>
                    ) : null}
                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setDrawerOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void handleConfirm()}
                            disabled={!selectedLocationId}
                            loading={deploying}
                        >
                            Confirm delivery
                        </Button>
                    </Group>
                </Stack>
            </Drawer>
        </>
    )
}
